import { Dispatch, SetStateAction, useContext, useEffect, useRef } from "react"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { CREATE_TASK_ENDPOINT, SERVER_URL, TASK_GIS_ENDPOINT } from "@pma/utils/api-routes"
import axios from "axios"
import { DictType } from "@pma/types"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { Form, FormControl, FormField, FormItem, FormMessage } from "@pma/components/common/form"
import { useWarnings } from "@pma/contexts/WarningsContext"

interface Props {
  projectId?: string
  task?: DictType
  gisData?: DictType
  closeGisPopUp?: Dispatch<SetStateAction<any>>
  group_id?: number | null | undefined
}

interface Task {
  id: number
  tasks_group_id: number | null
  title?: string
  created_at?: string | number | Date
  _subtasks?: Subtask[]
  [key: string]: any
}

interface Subtask {
  id: number
  parent_tasks_id: number
  [key: string]: any
}

interface Group {
  id: number
  name: string
  project_id: number
  user_id: number
  _tasks: Task[]
  created_at: string | null
}

const formSchema = z.object({
  title: z
    .string()
    .trim()
    .nonempty({ message: "New task is required" })
    .min(2, { message: "Task must be at least 2 characters long" })
    .max(250, { message: "Task can only be up to 250 characters long" }),
})

const AddTaskInput = ({ projectId, task, gisData, closeGisPopUp, group_id = null }: Props) => {
  const navigate = useNavigate()
  const { groupedTasks, setGroupedTasks } = useContext(GroupedTasksContext) as {
    groupedTasks: Group[]
    setGroupedTasks: (group: Group[]) => void
  }
  const { refetchWarnings } = useWarnings()
  const formRef = useRef<HTMLFormElement | null>(null)

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { translate } = useTranslator()

  const paramProjectId = useParams()?.projectId

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const currentTime = Date.now()

  const midnightTime = new Date().setHours(23, 59, 59, 999);

  const isSubItem = task && task?.id

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "" },
  })

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = form

  const addTask = async (payload: DictType) => {
    if (group_id) {
      payload["tasks_group_id"] = group_id > 0 ? group_id : 0
    }
    const parentData = isSubItem
      ? {
        parent_tasks_id: task?.id,
      }
      : {}
    const response = await axios.post(
      SERVER_URL + CREATE_TASK_ENDPOINT,
      {
        ...payload,
        ...parentData,
        start_at: currentTime,
        finish_to: midnightTime,
        project_id: projectId || paramProjectId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const linkTaskToGisPoint = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + TASK_GIS_ENDPOINT,
      {
        task_id: payload?.id,
        data: gisData,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const addTaskMutation = useMutation({
    mutationKey: ["add-task"],
    mutationFn: addTask,
    onSuccess: (data) => {
      if (gisData) {
        return linkTaskToGisPointMutation.mutate(data)
      } else {
        refetchWarnings()
        // Revalidate tasks when tasks are updated
        toast({
          title: translate("Success"),
          description: translate("Task added successfully"),
          variant: "success",
        })
        newParams.delete("priority")
        newParams.delete("user")
        newParams.delete("status")
        newParams.delete("from")
        newParams.delete("to")
        newParams.delete("tracker")
        newParams.delete("query")
        newParams.set("revalidate", "tasks")

        const newGroupedTasks = groupedTasks.map((group) => {
          if (isSubItem) {
            if (group?.id == group_id) {
              const newTaskList = group._tasks.map((task) => {
                if (task.id == isSubItem) {
                  task._subtasks = [data, ...(task._subtasks == undefined ? [] : task._subtasks)]
                }
                return task
              })
              group._tasks = newTaskList
            }
          } else {
            if (group?.id == group_id) group._tasks = [data, ...group._tasks]
          }
          return group
        })
        setGroupedTasks(newGroupedTasks)

        navigate(`?${newParams.toString()}`)
        reset()
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error adding task"),
        variant: "error",
      })
    },
  })

  const linkTaskToGisPointMutation = useMutation({
    mutationKey: ["add-task-gis-link"],
    mutationFn: linkTaskToGisPoint,
    onSuccess: (data) => {
      // Revalidate tasks
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      reset()
      closeGisPopUp && closeGisPopUp(null)

      toast({
        title: translate("Success"),
        description: translate("Task added successfully"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error adding task"),
        variant: "error",
      })
    },
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    addTaskMutation.mutate(data)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        reset() // Reset form values
      }
    }

    document.addEventListener("click", handleClickOutside)

    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [reset])

  return (
    <Form {...form}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        className={cn("my-2 flex items-center gap-4 pl-2", gisData && "my-4 w-full gap-1")}
      >
        <div
          className={cn(
            addTaskMutation.isPending &&
            "absolute h-9 animate-shimmer rounded-md bg-gradient-to-r from-black/10 to-slate-500/10 delay-100 duration-150 sm:w-[250px] md:w-[350px] lg:w-[600px]",
          )}
        />
        <FormField
          control={control}
          name='title'
          render={({ field }) => (
            <FormItem className='flex flex-col items-start justify-start space-y-2'>
              <FormControl>
                <Input
                  placeholder={translate(isSubItem ? "SubItem" : "New task")}
                  className={cn(
                    `h-9 sm:w-[250px] md:w-[350px] lg:w-[600px] ${errors?.title ? "border-red-500 focus-visible:ring-red-500" : ""}`,
                  )}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <Button size={"sm"} loading={addTaskMutation.isPending}>
          {translate(isSubItem ? "Add SubItem" : "Add Task")}
        </Button>
        {errors?.title ? (
          <p className='text-[0.8rem] font-medium text-destructive'>
            {translate(`${errors?.title?.message}`)}
          </p>
        ) : null}
        <FormMessage />
      </form>
    </Form>
  )
}

export default AddTaskInput
